import { Text, View } from '@react-pdf/renderer'
import { DatePicker, Form, Input } from 'antd'
import { CantileverButton } from 'components/CantileverButton'
import dayjs from 'dayjs'
import {
  FfsCoverSectionData,
  FfsMainInfo,
  FfsReportSectionId,
} from 'pages/reporting/reports/ffs/ffsReportData'
import { useFfsReportOps } from 'pages/reporting/reports/ffs/FfsReportProvider'
import GeckoLogo from 'pages/reporting/shared/pdfGeneration/components/shared/GeckoLogo'
import { GradientStripe } from 'pages/reporting/shared/pdfGeneration/components/shared/GradientStripe'
import { PageFooter } from 'pages/reporting/shared/pdfGeneration/components/shared/PageFooter'
import { ReportPage } from 'pages/reporting/shared/pdfGeneration/components/shared/ReportPage'
import { PDFStyles } from 'pages/reporting/shared/pdfGeneration/styles/PDFStyles'
import { formatDate } from 'pages/reporting/shared/pdfGeneration/utils/formatters'
import { StyledForm } from 'pages/reporting/shared/StyledComponents'
import { PdfProps, ReportSection } from 'pages/reporting/shared/types'
import React, { useMemo } from 'react'

const REPORT_DATE_FORMAT = 'MM/DD/YYYY'

const requiredCoverPageFields = [
  'mainInfo.customerName',
  'mainInfo.assetName',
  'coverSectionData.reportTitle',
  'coverSectionData.preparedBy',
  'coverSectionData.preparedFor',
]

const RenderPDF: React.FC<PdfProps> = ({ onRender }) => {
  const { formData } = useFfsReportOps()

  const signoff = useMemo(() => formData.signoff, [formData.signoff])
  const mainInfo = useMemo(() => formData.mainInfo, [formData.mainInfo])
  const coverData = useMemo(
    () => formData.coverSectionData,
    [formData.coverSectionData]
  )

  const isPrelim = useMemo(
    () =>
      !(
        formData.signoff?.reviewerSignoff && formData.signoff?.supervisorSignoff
      ),
    [formData.signoff?.reviewerSignoff, formData.signoff?.supervisorSignoff]
  )

  const reportTitle =
    coverData.reportTitle || 'Fitness-For-Service Assessment Report'

  return (
    <ReportPage
      id='cover'
      size='LETTER'
      wrap={false}
      tracker={{
        sectionId: FfsReportSectionId.COVER,
        onRender,
        registerOnce: false,
      }}
      contentStyle={[PDFStyles.content, { paddingBottom: 70 }]}
      footer={
        <PageFooter
          jobNumber={mainInfo.assetId}
          revisionNumber={formData.revisionMetadata.revisionNumber}
          prelim={isPrelim}
        />
      }>
      {/* Header with Gecko logo and gradient stripe */}
      <View style={PDFStyles.coverHeader}>
        <GeckoLogo />
        <View style={PDFStyles.coverHeaderContent}>
          <Text style={PDFStyles.coverHeaderText}>
            {`${isPrelim ? 'Preliminary ' : ''}${reportTitle}`}
          </Text>
          <GradientStripe />
        </View>
      </View>

      {/* Customer Section */}
      <View style={PDFStyles.coverPageSectionContainer}>
        <View style={PDFStyles.coverPageSectionTitle}>
          <View style={PDFStyles.coverPageDash} />
          <Text style={PDFStyles.coverPageTitleText}>Customer</Text>
        </View>
        <View style={PDFStyles.coverPageSectionContent}>
          <Text style={PDFStyles.coverPageValueTextLarge}>
            {mainInfo.customerName}
          </Text>
          <Text style={PDFStyles.coverPageValueTextLarge}>
            {mainInfo.customerLocation}
          </Text>
        </View>
      </View>

      {/* Asset Section */}
      <View style={PDFStyles.coverPageSectionContainer}>
        <View style={PDFStyles.coverPageSectionTitle}>
          <View style={PDFStyles.coverPageDash} />
          <Text style={PDFStyles.coverPageBoldTitleText}>Asset</Text>
        </View>
        <View style={PDFStyles.coverPageSectionContent}>
          <Text style={PDFStyles.coverPageValueText}>{mainInfo.assetName}</Text>
        </View>
      </View>

      {/* Prepared For Section */}
      <View style={PDFStyles.coverPageSectionContainer}>
        <View style={PDFStyles.coverPageSectionTitle}>
          <View style={PDFStyles.coverPageShortDash} />
          <Text style={PDFStyles.coverPageTitleText}>Prepared For</Text>
        </View>
        <View style={PDFStyles.coverPageSectionContent}>
          <Text style={PDFStyles.coverPageValueText}>
            {coverData.preparedFor}
          </Text>
        </View>
      </View>

      {/* Prepared By Section */}
      <View style={PDFStyles.coverPageSectionContainer}>
        <View style={PDFStyles.coverPageSectionTitle}>
          <View style={PDFStyles.coverPageShortDash} />
          <Text style={PDFStyles.coverPageTitleText}>Prepared By</Text>
        </View>
        <View style={PDFStyles.coverPageSectionContent}>
          <Text style={PDFStyles.coverPageValueText}>
            {coverData.preparedBy}
          </Text>
        </View>
      </View>

      {/* Report Number Section */}
      {coverData.reportNumber && (
        <View style={PDFStyles.coverPageSectionContainer}>
          <View style={PDFStyles.coverPageSectionTitle}>
            <View style={PDFStyles.coverPageShortDash} />
            <Text style={PDFStyles.coverPageTitleText}>Report Number</Text>
          </View>
          <View style={PDFStyles.coverPageSectionContent}>
            <Text style={PDFStyles.coverPageValueText}>
              {coverData.reportNumber}
            </Text>
          </View>
        </View>
      )}

      {/* Report Date Section */}
      <View style={PDFStyles.coverPageSectionContainer}>
        <View style={PDFStyles.coverPageSectionTitle}>
          <View style={PDFStyles.coverPageDash} />
          <Text style={PDFStyles.coverPageTitleText}>Report Date</Text>
        </View>
        <View style={PDFStyles.coverPageSectionContent}>
          <Text style={PDFStyles.coverPageValueText}>
            {mainInfo.reportDate
              ? dayjs(mainInfo.reportDate).format(REPORT_DATE_FORMAT)
              : 'Not specified'}
          </Text>
        </View>
      </View>

      {/* Signature Section */}
      <View style={PDFStyles.signatureSection}>
        <View style={PDFStyles.contributorRow}>
          <Text style={PDFStyles.contributorHeaderText}>Prepared By</Text>
          <Text style={PDFStyles.contributorHeaderText}>Date</Text>
          <Text style={PDFStyles.contributorHeaderText}>Signature</Text>
        </View>
        <View style={PDFStyles.contributorBox}>
          <Text style={PDFStyles.contributorText}>
            {signoff?.supervisorSignoff?.name || ''}
          </Text>
          <Text style={PDFStyles.contributorText}>
            {signoff?.supervisorSignoff?.timestamp
              ? formatDate(signoff.supervisorSignoff.timestamp)
              : ''}
          </Text>
          <Text style={PDFStyles.contributorText}></Text>
        </View>

        <View style={PDFStyles.contributorRow}>
          <Text style={PDFStyles.contributorHeaderText}>Reviewed By</Text>
          <Text style={PDFStyles.contributorHeaderText}>Date</Text>
          <Text style={PDFStyles.contributorHeaderText}>Signature</Text>
        </View>
        <View style={PDFStyles.contributorBox}>
          <Text style={PDFStyles.contributorText}>
            {signoff?.reviewerSignoff?.name || ''}
          </Text>
          <Text style={PDFStyles.contributorText}>
            {signoff?.reviewerSignoff?.timestamp
              ? formatDate(signoff.reviewerSignoff.timestamp)
              : ''}
          </Text>
          <Text style={PDFStyles.contributorText}></Text>
        </View>
      </View>
    </ReportPage>
  )
}

const RenderReact: React.FC = () => {
  const [form] = Form.useForm<
    FfsMainInfo &
      FfsCoverSectionData & {
        reportDate: dayjs.Dayjs
      }
  >()
  const { formData, handleCoverSectionFinish, handleMainInfoFinish } =
    useFfsReportOps()

  const initialValues = React.useMemo(
    () => ({
      ...formData.mainInfo,
      ...formData.coverSectionData,
      reportDate: formData.mainInfo.reportDate
        ? dayjs(formData.mainInfo.reportDate)
        : dayjs(),
    }),
    [formData.mainInfo, formData.coverSectionData]
  )

  const onFinish = async (
    values: FfsMainInfo &
      FfsCoverSectionData & {
        reportDate: dayjs.Dayjs
      }
  ) => {
    const processedValues = {
      ...values,
      reportDate: values.reportDate?.toISOString() || new Date().toISOString(),
    }

    const {
      customerName,
      customerLocation,
      assetName,
      assetId,
      reportDate,
      ...coverData
    } = processedValues

    const mainInfoUpdate: FfsMainInfo = {
      customerName,
      customerLocation,
      assetName,
      assetId,
      reportDate,
    }

    await handleCoverSectionFinish(coverData)
    await handleMainInfoFinish(mainInfoUpdate)
  }

  return (
    <StyledForm
      form={form}
      name='ffsCoverPageForm'
      onFinish={onFinish}
      layout='vertical'
      initialValues={initialValues}>
      <Form.Item
        name='reportTitle'
        label='Report Title'
        rules={[{ required: true, message: 'Please enter report title' }]}>
        <Input placeholder='Fitness-For-Service Assessment Report' />
      </Form.Item>

      <Form.Item
        name='customerName'
        label='Customer Name'
        rules={[{ required: true, message: 'Please enter customer name' }]}>
        <Input />
      </Form.Item>

      <Form.Item name='customerLocation' label='Customer Location'>
        <Input />
      </Form.Item>

      <Form.Item
        name='assetName'
        label='Asset Name'
        rules={[{ required: true, message: 'Please enter asset name' }]}>
        <Input />
      </Form.Item>

      <Form.Item name='assetId' label='Asset ID / Job Number'>
        <Input />
      </Form.Item>

      <Form.Item
        name='preparedFor'
        label='Prepared For'
        rules={[{ required: true, message: 'Please enter prepared for' }]}>
        <Input />
      </Form.Item>

      <Form.Item
        name='preparedBy'
        label='Prepared By'
        rules={[{ required: true, message: 'Please enter prepared by' }]}>
        <Input />
      </Form.Item>

      <Form.Item name='reportNumber' label='Report Number'>
        <Input />
      </Form.Item>

      <Form.Item name='revisionNumber' label='Revision Number'>
        <Input />
      </Form.Item>

      <Form.Item
        name='reportDate'
        label='Report Date'
        rules={[{ required: true, message: 'Please select report date' }]}>
        <DatePicker style={{ width: '100%' }} />
      </Form.Item>

      <Form.Item>
        <CantileverButton
          primary
          onClick={() => form.submit()}
          style={{ width: '100%' }}>
          Save Cover Page
        </CantileverButton>
      </Form.Item>
    </StyledForm>
  )
}

export const Cover: ReportSection = {
  RenderReact,
  required: true,
  RenderPDF,
  id: FfsReportSectionId.COVER,
  description: 'Cover page with report information and metadata',
  title: 'Cover Page',
  requiredFields: requiredCoverPageFields,
}
